package org.matthewo.components

import emotion.react.css
import js.objects.unsafeJso
import org.matthewo.styles.BACKGROUND_COLOR
import org.matthewo.styles.FONT_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import org.matthewo.styles.span
import react.*
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.dialog
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import web.cssom.*
import web.dom.ElementId
import web.html.*
import kotlin.reflect.KMutableProperty1

data class DialogField<T>(val field: KMutableProperty1<T, String>, val title: String, val password: Boolean = false)

external interface DialogProps<T : Any> : Props {
    var title: String
    var open: Boolean
    var onSubmit: (T) -> Unit
    var onCancel: () -> Unit
    var validate: ((T) -> Boolean)?
}

fun <T : Any> Dialog(fields: List<DialogField<T>>) = FC<DialogProps<T>> { props ->
    val dialogRef = useRef<HTMLDialogElement>()

    val inputs = fields.map { useState("") }

    fun submit() {
        val state = unsafeJso<T>()
        fields.zip(inputs) { field, (input, _) ->
            field.field.set(state, input)
        }
        if (props.validate?.invoke(state) != false) {
            props.onSubmit(state)
        }
    }

    useEffect(props.open) {
        inputs.forEach { (_, setInput) ->
            setInput("")
        }
        if (props.open) dialogRef.current?.showModal()
        else dialogRef.current?.close()
    }

    dialog {
        ref = dialogRef

        css {
            border = Border(2.px, LineStyle.solid, TITLE_FONT_COLOR)
            borderRadius = 0.5.em
            padding = 2.em

            backgroundColor = BACKGROUND_COLOR
            color = FONT_COLOR

            backdrop {
                backgroundColor = rgb(0, 0, 0, 0.5)
                backdropFilter = blur(4.px)
            }
        }

        onClose = {
            props.onCancel()
        }

        form {
            css {
                display = Display.grid
                gridTemplateColumns = array(Auto.auto, Auto.auto)
                gridTemplateRows = repeat(2 + fields.size, 1.fr)
                gap = 1.em
            }

            h3 {
                css {
                    padding = 0.px
                    margin = 0.px
                    gridColumn = span(2)
                    textAlign = TextAlign.center
                    color = TITLE_FONT_COLOR
                    userSelect = None.none
                }
                +props.title
            }

            fields.zip(inputs) { field, (input, setInput) ->
                val elementId = ElementId(field.title.lowercase())
                label {
                    css {
                        display = Display.flex
                        alignItems = AlignItems.center
                        userSelect = None.none
                    }
                    htmlFor = elementId
                    +"${field.title}:"
                }
                input {
                    css {
                        color = BACKGROUND_COLOR
                        borderRadius = 0.5.em
                        border = None.none
                    }
                    id = elementId
                    type = if (field.password) InputType.password else InputType.text
                    value = input
                    onChange = { setInput(it.target.value) }
                    onSubmit = {
                        it.preventDefault()
                        submit()
                    }
                }
            }

            div {
                css {
                    gridColumn = span(2)
                    display = Display.flex
                    justifyContent = JustifyContent.spaceAround
                }

                button {
                    css {
                        backgroundColor = NamedColor.red
                        color = BACKGROUND_COLOR
                        userSelect = None.none
                        border = None.none
                        borderRadius = 0.5.em
                    }
                    onClick = {
                        it.preventDefault()
                        props.onCancel()
                    }
                    type = ButtonType.button
                    +"Cancel"
                }

                button {
                    css {
                        backgroundColor = NamedColor.green
                        color = BACKGROUND_COLOR
                        userSelect = None.none
                        border = None.none
                        borderRadius = 0.5.em
                    }
                    onClick = {
                        it.preventDefault()
                        submit()
                    }
                    type = ButtonType.submit
                    +"Submit"
                }
            }
        }
    }
}
